import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { merge, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take } from 'rxjs/operators';

import { MyWorkPermitsHttpService } from '@core/http/my-work-permits.http.service';
import { MODULE_KEY, ModulesConfiguration } from '@interfaces';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import * as routerActions from '@state/router/router.actions';
import * as systemSettingsActions from '@state/system-settings/system-settings.actions';
import { SystemSettingsFacade } from '@state/system-settings/system-settings.facade';
import * as actions from './my-work-permits.actions';

// API error keys of the foreigner documents endpoints that have a dedicated translation.
const KNOWN_DOCUMENT_ERRORS = [
  'WORK_PERMIT_DOCUMENT_APPROVED',
  'WORK_PERMIT_DOCUMENT_SIZE_LIMIT_EXCEEDED',
  'WORK_PERMIT_DOCUMENT_NOT_FOUND',
  'WORK_PERMIT_DOCUMENT_NOT_EDITABLE_BY_FOREIGNER',
  'WORK_PERMIT_DOCUMENT_FILE_NOT_OWNED',
  'FORBIDDEN',
];

const documentErrorMessageKey = (error: HttpErrorResponse): string => {
  if (error?.status === 413) {
    return 'WORK_PERMITS.MY_WORK_PERMIT.ERRORS.FILE_TOO_BIG';
  }
  const key = error?.error?.message;
  return typeof key === 'string' && KNOWN_DOCUMENT_ERRORS.includes(key)
    ? `WORK_PERMITS.MY_WORK_PERMIT.ERRORS.${key}`
    : 'WORK_PERMITS.MY_WORK_PERMIT.ERRORS.GENERIC';
};

@Injectable()
export class MyWorkPermitsEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(MyWorkPermitsHttpService);
  private readonly stackedMessages = inject(StackedMessagesService);
  private readonly systemSettingsFacade = inject(SystemSettingsFacade);

  // A disabled WORK_PERMIT module behaves as if the foreigner had no work-permits.
  getMyWorkPermits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getMyWorkPermits),
      switchMap(() =>
        // Modules are enabled by default, also when their configuration failed to load.
        merge(
          this.systemSettingsFacade.modulesConfiguration$.pipe(filter(modulesConfiguration => !!modulesConfiguration)),
          this.actions$.pipe(
            ofType(systemSettingsActions.getModulesConfigurationError),
            map((): ModulesConfiguration => ({}))
          )
        ).pipe(take(1))
      ),
      switchMap(modulesConfiguration => {
        if (modulesConfiguration[MODULE_KEY.WORK_PERMIT] === false) {
          return of(actions.getMyWorkPermitsSuccess({ myWorkPermits: [] }));
        }
        return this.http.getMyWorkPermits().pipe(
          map(myWorkPermits => actions.getMyWorkPermitsSuccess({ myWorkPermits })),
          catchError(() => of(actions.getMyWorkPermitsError()))
        );
      })
    )
  );

  getMyWorkPermit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getMyWorkPermit),
      switchMap(({ workPermitId }) =>
        this.http.getMyWorkPermit(workPermitId).pipe(
          map(myWorkPermit => actions.getMyWorkPermitSuccess({ myWorkPermit })),
          catchError(() => of(actions.getMyWorkPermitError({ workPermitId })))
        )
      )
    )
  );

  // The work-permit is gone (unlinked / deleted) - refresh the list and let the landing rules pick a new view.
  getMyWorkPermitError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getMyWorkPermitError),
      switchMap(() => {
        this.stackedMessages.showError({ message: 'WORK_PERMITS.MY_WORK_PERMIT.NOT_AVAILABLE' });
        return [actions.getMyWorkPermits(), routerActions.changeRoute({ linkParams: ['/user-process'] })];
      })
    )
  );

  uploadMyWorkPermitDocumentFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.uploadMyWorkPermitDocumentFile),
      mergeMap(({ uploadId, workPermitId, documentId, file }) =>
        this.http.uploadDocumentFile(workPermitId, documentId, file).pipe(
          map(() => actions.uploadMyWorkPermitDocumentFileSuccess({ uploadId, workPermitId, documentId })),
          catchError((error: HttpErrorResponse) => {
            this.stackedMessages.showError({
              message: documentErrorMessageKey(error),
              translateParams: { fileName: file.name },
            });
            return of(actions.uploadMyWorkPermitDocumentFileError({ uploadId, workPermitId, documentId }));
          })
        )
      )
    )
  );

  removeMyWorkPermitDocumentFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.removeMyWorkPermitDocumentFile),
      mergeMap(({ workPermitId, documentId, file }) =>
        this.http.removeDocumentFile(workPermitId, documentId, file.id).pipe(
          map(() => {
            this.stackedMessages.showSuccess({
              message: 'WORK_PERMITS.MY_WORK_PERMIT.FILES.REMOVE_SUCCESS',
              translateParams: { fileName: file.fileOriginalName },
            });
            return actions.removeMyWorkPermitDocumentFileSuccess({ workPermitId, userAssetId: file.id });
          }),
          catchError((error: HttpErrorResponse) => {
            this.stackedMessages.showError({ message: documentErrorMessageKey(error) });
            return of(actions.removeMyWorkPermitDocumentFileError({ userAssetId: file.id }));
          })
        )
      )
    )
  );

  // Files change document statuses and the work-permit's `updatedAt` (used by the landing rules).
  reloadAfterFileChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.uploadMyWorkPermitDocumentFileSuccess,
        actions.uploadMyWorkPermitDocumentFileError,
        actions.removeMyWorkPermitDocumentFileSuccess
      ),
      switchMap(({ workPermitId }) => [actions.getMyWorkPermit({ workPermitId }), actions.getMyWorkPermits()])
    )
  );
}
